import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useTime } from "@/app/application/TimeContext";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { TranslationContext } from "@/app/providers";
import { Task } from "@/features/task/model/Task/TaskRequest";
import { Language } from "@/model/Language/Language";
import { formatUtcDateTime } from "@/shared/lib";
import {
	SearchPaletteAttributeList,
	SearchPaletteQuickLookPanel,
	SearchPaletteSectionHeader,
} from "@/shared/ui/search-palette/SearchPaletteBlocks";
import {
	searchPaletteAccentChipSx,
	searchPaletteInputSx,
	searchPaletteMetaTextSx,
	searchPaletteNeutralChipSx,
	searchPalettePaperSx,
	searchPaletteQuickLookToggleSx,
	searchPaletteRowSx,
} from "@/shared/ui/search-palette/searchPaletteStyles";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import LaunchIcon from "@mui/icons-material/Launch";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	Stack,
	TextField,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { useFetchTasks } from "@/service/TaskQueries";

interface TaskGlobalSearchDialogProps {
	open: boolean;
	onClose: () => void;
	selectedGroupIds: number[];
	onOpenTab: (
		tab: "active" | "completed" | "recurring",
		taskId?: number,
	) => void;
}

type ResultStatus = "Active" | "Archived" | "Recurring";
type SearchResult = Task & { status: ResultStatus };
type FilterKey = "Active" | "Archived" | "Recurring";
type TaskColorKey = ResultStatus | FilterKey;

interface TaskListResponse {
	Items: Task[];
}

const statusToTab: Record<ResultStatus, "active" | "completed" | "recurring"> =
	{
		Active: "active",
		Archived: "completed",
		Recurring: "recurring",
	};

const statusToLabel: Record<ResultStatus, string> = {
	Active: "Active",
	Archived: "Completed",
	Recurring: "Recurring",
};

const stripHtml = (value?: string | null) =>
	(value || "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const clampText = (value?: string | null, max = 140) => {
	const cleaned = stripHtml(value);
	if (!cleaned) return "";
	return cleaned.length > max
		? `${cleaned.slice(0, max - 1).trimEnd()}…`
		: cleaned;
};

function getTaskStatusAccent(theme: Theme, status: TaskColorKey) {
	switch (status) {
		case "Active":
			return theme.palette.primary.main;
		case "Archived":
			return theme.palette.success.main;
		case "Recurring":
			return theme.palette.warning.main;
		default:
			return theme.palette.text.secondary;
	}
}

function getTaskStatusTint(
	theme: Theme,
	status: TaskColorKey,
	opacity: number,
) {
	return alpha(getTaskStatusAccent(theme, status), opacity);
}

function getTaskMetadata(task: SearchResult, timezone: string) {
	const tokens: string[] = [];
	if (task.status === "Archived" && task.Completed) {
		tokens.push(`Completed ${formatUtcDateTime(task.Completed, timezone)}`);
	} else if (task.Deadline) {
		tokens.push(`Due ${formatUtcDateTime(task.Deadline, timezone)}`);
	}
	if (task.Reccuring) {
		tokens.push(
			task.ReccuringConfiguration?.Frequency
				? `Repeats ${task.ReccuringConfiguration.Frequency}`
				: "Recurring",
		);
	}
	if (task.Handover) tokens.push("Handover");
	if (task.Shiftlog) tokens.push("Shift log");
	return tokens;
}

function getTaskResponsible(task: SearchResult) {
	return task.User?.Name || task.CompletedBy?.Name || "";
}


export const TaskGlobalSearchDialog: FC<TaskGlobalSearchDialogProps> = ({
	open,
	onClose,
	selectedGroupIds,
	onOpenTab,
}) => {
	const theme = useTheme();
	const translation: Language | null = useContext(TranslationContext);
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
	const { timezone } = useTime();

	const [query, setQuery] = useState("");
	const [filters, setFilters] = useState<Set<FilterKey>>(new Set());
	const [activeIndex, setActiveIndex] = useState(0);
	const [quickLookOpen, setQuickLookOpen] = useState(false);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const listRef = useRef<HTMLUListElement | null>(null);
	const searchTerm = query.trim();
	const effectiveFilters = filters;
	const enabled = open && selectedGroupIds.length > 0 && searchTerm.length >= 2;
	const fetchActive = enabled;
	const fetchCompleted = enabled;
	const fetchRecurring = enabled;

	const activeResults = useFetchTasks(
		selectedGroupIds,
		"Active",
		1,
		8,
		enabled ? searchTerm : undefined,
		undefined,
		fetchActive,
	) as unknown as { data?: TaskListResponse; isFetching?: boolean };
	const completedResults = useFetchTasks(
		selectedGroupIds,
		"Archived",
		1,
		8,
		enabled ? searchTerm : undefined,
		undefined,
		fetchCompleted,
	) as unknown as { data?: TaskListResponse; isFetching?: boolean };
	const recurringResults = useFetchTasks(
		selectedGroupIds,
		"Recurring",
		1,
		8,
		enabled ? searchTerm : undefined,
		undefined,
		fetchRecurring,
	) as unknown as { data?: TaskListResponse; isFetching?: boolean };

	const isFetching = Boolean(
		(fetchActive && activeResults.isFetching) ||
		(fetchCompleted && completedResults.isFetching) ||
		(fetchRecurring && recurringResults.isFetching),
	);

	useEffect(() => {
		if (!open) return;
		setQuery("");
		setFilters(new Set());
		setActiveIndex(0);
		setQuickLookOpen(false);
		const timer = window.setTimeout(() => inputRef.current?.focus(), 30);
		return () => window.clearTimeout(timer);
	}, [open]);

	const results = useMemo(() => {
		if (!enabled) return [];

		return [
			...(fetchActive
				? (activeResults.data?.Items ?? []).map((task) => ({
						...task,
						status: "Active" as ResultStatus,
					}))
				: []),
			...(fetchCompleted
				? (completedResults.data?.Items ?? []).map((task) => ({
						...task,
						status: "Archived" as ResultStatus,
					}))
				: []),
			...(fetchRecurring
				? (recurringResults.data?.Items ?? []).map((task) => ({
						...task,
						status: "Recurring" as ResultStatus,
					}))
				: []),
		] as SearchResult[];
	}, [
		enabled,
		fetchActive,
		fetchCompleted,
		fetchRecurring,
		activeResults.data?.Items,
		completedResults.data?.Items,
		recurringResults.data?.Items,
	]);

	const toggleFilter = (key: FilterKey) => {
		setFilters((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const clearFilters = () => setFilters(new Set());

	const filteredResults = useMemo(
		() =>
			results.filter(
				(item) =>
					effectiveFilters.size === 0 || effectiveFilters.has(item.status),
			),
		[results, effectiveFilters],
	);

	const { groups, flat } = useMemo(() => {
		const orderedStatuses: ResultStatus[] = ["Active", "Archived", "Recurring"];
		const flatItems: SearchResult[] = [];
		const grouped: Array<{
			group: ResultStatus;
			items: Array<{ item: SearchResult; index: number }>;
		}> = [];

		for (const status of orderedStatuses) {
			const statusItems = filteredResults.filter(
				(item) => item.status === status,
			);
			if (!statusItems.length) continue;

			const indexedItems = statusItems.map((item) => {
				const index = flatItems.length;
				flatItems.push(item);
				return { item, index };
			});

			grouped.push({ group: status, items: indexedItems });
		}

		return { groups: grouped, flat: flatItems };
	}, [filteredResults]);

	const activeItem = flat[activeIndex] ?? null;

	useEffect(() => {
		setActiveIndex(0);
	}, [query, filters, open]);

	useEffect(() => {
		const ul = listRef.current;
		if (!ul) return;
		const el = ul.querySelector(`[data-idx="${activeIndex}"]`);
		el?.scrollIntoView({ block: "nearest" });
	}, [activeIndex]);

	const handleClose = () => {
		setQuery("");
		setFilters(new Set());
		setActiveIndex(0);
		setQuickLookOpen(false);
		onClose();
	};

	const openItem = (item: SearchResult) => {
		onOpenTab(statusToTab[item.status], item.TaskId);
		handleClose();
	};

	const openItemNewTab = (item: SearchResult) => {
		window.open(
			`/tasks?tab=${statusToTab[item.status]}&taskId=${item.TaskId}`,
			"_blank",
			"noopener,noreferrer",
		);
	};

	const emptyState = useMemo(() => {
		if (selectedGroupIds.length === 0)
			return "Select at least one task group to search";
		if (!query.trim()) return "Search active, completed, or recurring tasks";
		if (searchTerm.length < 2) return "Enter at least 2 characters";
		if (isFetching && filteredResults.length === 0) return "Searching…";
		if (
			results.length > 0 &&
			filteredResults.length === 0 &&
			effectiveFilters.size > 0
		)
			return "No tasks in selected filters";
		return "No tasks matched this search";
	}, [
		selectedGroupIds.length,
		query,
		searchTerm.length,
		isFetching,
		filteredResults.length,
		results.length,
		effectiveFilters.size,
	]);

	const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Escape") {
			e.preventDefault();
			handleClose();
			return;
		}
		if (!flat.length) return;

		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((i) => Math.min(i + 1, Math.max(0, flat.length - 1)));
			return;
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((i) => Math.max(i - 1, 0));
			return;
		}
		if (e.key === "Enter" && e.shiftKey) {
			e.preventDefault();
			if (isMdUp) setQuickLookOpen((v) => !v);
			return;
		}
		if (e.key === "Enter") {
			e.preventDefault();
			if (!activeItem) return;
			if (e.metaKey || e.ctrlKey) {
				openItemNewTab(activeItem);
				handleClose();
				return;
			}
			openItem(activeItem);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			sx={{
				"& .MuiDialog-container": {
					alignItems: "flex-start",
					paddingTop: { xs: 1.5, sm: 3 },
				},
			}}
			PaperProps={{
				sx: (t) => searchPalettePaperSx(t, isMobile),
			}}
		>
			<DialogTitle
				component="div"
				sx={{
					px: 2,
					pt: 1.5,
					pb: 1,
				}}
			>
				<Typography sx={{ fontWeight: 900, letterSpacing: 0.2, mb: 1 }}>
					<Translate String="TaskGlobalSearchDialog.Title">
						Global search
					</Translate>
				</Typography>

				<TextField
					fullWidth
					placeholder={GetLanguageValueAsString(
						translation,
						"TaskGlobalSearchDialog.Placeholder",
						"Search title or description",
					)}
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					onKeyDown={onInputKeyDown}
					inputRef={inputRef}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position="end">
								{isFetching ? (
									<CircularProgress size={16} sx={{ mr: 1 }} />
								) : null}
								{query ? (
									<IconButton
										size="small"
										aria-label={GetLanguageValueAsString(
											translation,
											"Common.Clear",
											"Clear",
										)}
										onClick={() => setQuery("")}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								) : null}
							</InputAdornment>
						),
					}}
					sx={(t) => searchPaletteInputSx(t)}
				/>

				{searchTerm.length >= 2 && selectedGroupIds.length > 0 && (
					<Stack
						direction="row"
						spacing={0.75}
						alignItems="center"
						flexWrap="wrap"
						useFlexGap
						sx={{ mt: 1 }}
					>
						<Chip
							label={GetLanguageValueAsString(translation, "Common.All", "All")}
							clickable
							variant="outlined"
							onClick={clearFilters}
							sx={searchPaletteNeutralChipSx(theme, effectiveFilters.size === 0)}
						/>
						<Chip
							label={GetLanguageValueAsString(
								translation,
								"Task.Status.Active",
								"Active",
							)}
							clickable
							variant="outlined"
							onClick={() => toggleFilter("Active")}
							sx={searchPaletteAccentChipSx(
								theme,
								getTaskStatusAccent(theme, "Active"),
								effectiveFilters.has("Active"),
							)}
						/>
						<Chip
							label={GetLanguageValueAsString(
								translation,
								"Task.Status.Completed",
								"Completed",
							)}
							clickable
							variant="outlined"
							onClick={() => toggleFilter("Archived")}
							sx={searchPaletteAccentChipSx(
								theme,
								getTaskStatusAccent(theme, "Archived"),
								effectiveFilters.has("Archived"),
							)}
						/>
						<Chip
							label={GetLanguageValueAsString(
								translation,
								"Task.Status.Recurring",
								"Recurring",
							)}
							clickable
							variant="outlined"
							onClick={() => toggleFilter("Recurring")}
							sx={searchPaletteAccentChipSx(
								theme,
								getTaskStatusAccent(theme, "Recurring"),
								effectiveFilters.has("Recurring"),
							)}
						/>
						{results.length > 0 && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ ml: 0.5, fontWeight: 800 }}
							>
								{`${filteredResults.length} shown of ${results.length} matches`}
							</Typography>
						)}
					</Stack>
				)}
			</DialogTitle>

			<Divider />

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: quickLookOpen && isMdUp ? "420px 1fr" : "1fr",
					height: flat.length > 0 ? { xs: "52vh", sm: "46vh" } : "auto",
					minHeight: flat.length > 0 ? 300 : "auto",
				}}
			>
				<Box
					sx={{
						px: 1.25,
						py: flat.length === 0 ? 0.75 : 1.25,
						overflow: "auto",
						minHeight: 0,
					}}
				>
					{flat.length === 0 ? (
						<Box sx={{ px: 1, py: 0.75 }}>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontWeight: 700 }}
							>
								{emptyState}
							</Typography>
						</Box>
					) : (
						<List
							ref={listRef}
							disablePadding
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 0.75,
								minHeight: 0,
							}}
						>
							{groups.map((group) => {
								const status = group.group;
								return (
									<Box key={status}>
										<SearchPaletteSectionHeader
											label={statusToLabel[status]}
											count={group.items.length}
											accentColor={getTaskStatusAccent(theme, status)}
										/>

										<Stack spacing={0.75}>
											{group.items.map(({ item: task, index: idx }) => {
												const selected = idx === activeIndex;
												const metadata = getTaskMetadata(task, timezone);
												const summary = clampText(task.Description, 90);
												const responsible = getTaskResponsible(task);

												return (
													<ListItemButton
														key={`${task.status}-${task.TaskId}`}
														data-idx={idx}
														onMouseEnter={() => setActiveIndex(idx)}
														onMouseOver={() => setActiveIndex(idx)}
														onMouseMove={() => setActiveIndex(idx)}
														onFocus={() => setActiveIndex(idx)}
														onClick={() => openItem(task)}
														sx={(t) => searchPaletteRowSx(t, selected)}
													>
														<Box
															sx={{
																width: 6,
																borderRadius: 2,
																bgcolor: getTaskStatusTint(
																	theme,
																	task.status,
																	0.55,
																),
																flex: "0 0 auto",
															}}
														/>

														<Box sx={{ flex: 1, minWidth: 0 }}>
															<Typography
																sx={{
																	fontWeight: 900,
																	display: "-webkit-box",
																	WebkitLineClamp: 2,
																	WebkitBoxOrient: "vertical" as const,
																	overflow: "hidden",
																	lineHeight: 1.2,
																}}
																title={task.Title}
															>
																{task.Title}
															</Typography>

															<Typography
																variant="caption"
																color="text.secondary"
																sx={{
																	mt: 0.45,
																	...searchPaletteMetaTextSx,
																}}
																title={[...metadata, responsible ? `By ${responsible}` : "", summary]
																	.filter(Boolean)
																	.join(" · ")}
															>
																{metadata.length > 0
																	? metadata.join(" · ")
																	: responsible
																		? `By ${responsible}`
																		: summary || (
																				<Translate String="Common.NoDescription">
																					No description
																				</Translate>
																			)}
															</Typography>
														</Box>

														<Stack
															direction="row"
															spacing={0.75}
															alignItems="flex-start"
														>
															<Chip
																size="small"
																label={statusToLabel[task.status]}
																variant="outlined"
																sx={{
																	borderRadius: 2,
																	fontWeight: 900,
																	height: 24,
																	bgcolor: getTaskStatusTint(
																		theme,
																		task.status,
																		0.08,
																	),
																	borderColor: getTaskStatusTint(
																		theme,
																		task.status,
																		0.24,
																	),
																	color: getTaskStatusAccent(theme, task.status),
																}}
															/>

															<IconButton
																className="hover-actions"
																size="small"
																aria-label={GetLanguageValueAsString(
																	translation,
																	"Common.OpenInNewTab",
																	"Open in new tab",
																)}
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	openItemNewTab(task);
																}}
																sx={(t) => ({
																	opacity: 0,
																	transition: "opacity 120ms ease",
																	mt: "-2px",
																	color: alpha(t.palette.text.primary, 0.55),
																	"&:hover": {
																		color: alpha(t.palette.text.primary, 0.9),
																	},
																})}
															>
																<LaunchIcon fontSize="inherit" />
															</IconButton>
														</Stack>
													</ListItemButton>
												);
											})}
										</Stack>
									</Box>
								);
							})}
						</List>
					)}
				</Box>

				{quickLookOpen && isMdUp && (
					<SearchPaletteQuickLookPanel
						accentColor={
							activeItem
								? getTaskStatusAccent(theme, activeItem.status)
								: theme.palette.primary.main
						}
						label={activeItem ? statusToLabel[activeItem.status] : "Task"}
						title={activeItem?.Title}
						body={
							activeItem ? (
								<Typography sx={{ whiteSpace: "pre-wrap" }} color="text.secondary">
									{stripHtml(activeItem.Description) || (
										<Translate String="Common.NoDescription">
											No description
										</Translate>
									)}
								</Typography>
							) : undefined
						}
						meta={
							activeItem ? (
								<Stack spacing={1.25}>
									<SearchPaletteAttributeList
										items={[
											{ label: "Status", value: statusToLabel[activeItem.status] },
											{
												label: activeItem.status === "Archived" ? "Completed" : "Due",
												value:
													activeItem.status === "Archived"
														? formatUtcDateTime(activeItem.Completed, timezone)
														: formatUtcDateTime(activeItem.Deadline, timezone),
											},
											{
												label: "Repeats",
												value: activeItem.Reccuring
													? activeItem.ReccuringConfiguration?.Frequency || "Recurring"
													: "",
											},
											{
												label: "Type",
												value:
													activeItem.Handover && activeItem.Shiftlog
														? "Handover · Shift log"
														: activeItem.Handover
															? "Handover"
															: activeItem.Shiftlog
																? "Shift log"
																: "",
											},
											{
												label: "Owner",
												value: getTaskResponsible(activeItem),
											},
										]}
									/>
									{activeItem.Tags?.length > 0 ? (
										<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
											{activeItem.Tags.slice(0, 3).map((tag) => (
												<Typography
													key={tag}
													variant="caption"
													color="text.secondary"
													sx={(t) => ({
														px: 1,
														py: 0.5,
														borderRadius: 999,
														border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
														bgcolor: alpha(t.palette.text.primary, 0.02),
													})}
												>
													{tag}
												</Typography>
											))}
											{activeItem.Tags.length > 3 ? (
												<Typography variant="caption" color="text.secondary">
													{`+${activeItem.Tags.length - 3} more`}
												</Typography>
											) : null}
										</Stack>
									) : null}
								</Stack>
							) : undefined
						}
						bodyLabel="Summary"
						metaLabel="Attributes"
						actions={
							activeItem ? (
								<Stack direction="row" spacing={1}>
									<Button
										variant="contained"
										startIcon={<KeyboardReturnIcon />}
										onClick={() => openItem(activeItem)}
										sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
									>
										Open
									</Button>
									<Button
										variant="outlined"
										startIcon={<LaunchIcon />}
										onClick={() => {
											openItemNewTab(activeItem);
											handleClose();
										}}
										sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
									>
										New tab
									</Button>
								</Stack>
							) : undefined
						}
					/>
				)}
			</Box>

			{isMdUp ? (
				<>
					<Divider />
					<Box
						sx={(t) => ({
							px: 2,
							py: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							bgcolor: t.palette.background.paper,
						})}
					>
						<Button
							size="small"
							variant="contained"
							startIcon={<PreviewIcon fontSize="small" />}
							onClick={() => setQuickLookOpen((value) => !value)}
							sx={(t) => searchPaletteQuickLookToggleSx(t, quickLookOpen)}
						>
							<Translate String="Common.QuickLook">Preview</Translate>
						</Button>
					</Box>
				</>
			) : null}
		</Dialog>
	);
};
